import { DateTime } from 'luxon';
import { useState } from 'react';

import { artiaProtectedApi } from '@/lib/api-client';
import { buildQueryString } from '@/utils/filtering-functions';

type CalendarExportFilters = {
  month: string;
  day?: string;
  tracked?: boolean;
  priority?: boolean;
  states?: string[];
  meetingTypes?: number[];
};

type UseCalendarExportOptions = {
  filters: CalendarExportFilters;
};

export const useCalendarExport = ({ filters }: UseCalendarExportOptions) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportCalendar = async () => {
    setIsExporting(true);
    
    try {
      // Generate filename based on filters
      const monthYear = DateTime.fromFormat(filters.month, 'yyyy-MM').toFormat('MMMM-yyyy');
      const dateStr = new Date().toISOString().split('T')[0];
      
      let filename = `calendar-${monthYear}`;
      
      if (filters.tracked && filters.priority) {
        filename += '-priority-tracked';
      } else if (filters.tracked) {
        filename += '-tracked';
      } else if (filters.priority) {
        filename += '-priority';
      }
      
      if (filters.day) {
        const dayStr = DateTime.fromISO(filters.day).toFormat('MMM-dd');
        filename += `-${dayStr}`;
      }
      
      filename += `-${dateStr}.csv`;

      // Check if showSaveFilePicker is available (modern browsers)
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: 'CSV files',
                accept: {
                  'text/csv': ['.csv'],
                },
              },
            ],
          });

          // Make the API call to get the CSV data
          const response = await artiaProtectedApi.get(
            `calendar/export/csv${buildQueryString(filters)}`,
            {
              responseType: 'blob'
            }
          );

          // Write the blob to the selected file
          const writable = await fileHandle.createWritable();
          await writable.write(response.data);
          await writable.close();

          return { success: true, method: 'save-picker' };
        } catch (error) {
          // User cancelled the save dialog or other error
          if ((error as Error).name === 'AbortError') {
            return { success: false, cancelled: true };
          }
          // Fall through to traditional download method
          console.warn('Save picker failed, falling back to download:', error);
        }
      }

      // Fallback to traditional download method
      const response = await artiaProtectedApi.get(
        `calendar/export/csv${buildQueryString(filters)}`,
        {
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, method: 'download' };
    } catch (error) {
      console.error('Calendar export error:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportCalendar,
    isExporting
  };
};
